"use client"

import Image from "next/image"
import { useEffect, useRef, useState } from "react"

import { VideoAutoplayFallback } from "@/shared/components/video-autoplay-fallback"

export function WizardPreviewVideo({
  mp4,
  poster,
  label,
  playing = true,
}: {
  mp4: string
  poster?: string
  label: string
  playing?: boolean
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [playingMp4, setPlayingMp4] = useState<string | null>(null)
  const [autoplayBlocked, setAutoplayBlocked] = useState(false)
  const hasStartedPlayback = playing && playingMp4 === mp4
  const showPoster = !!poster && !hasStartedPlayback

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    let cancelled = false
    setAutoplayBlocked(false)

    if (playing) {
      void video.play().catch(() => {
        if (cancelled) return
        setPlayingMp4(null)
        setAutoplayBlocked(true)
      })
    } else {
      video.pause()
      video.currentTime = 0
      setPlayingMp4(null)
    }

    return () => {
      cancelled = true
    }
  }, [playing, mp4])

  const handlePlaying = () => {
    setPlayingMp4(mp4)
    setAutoplayBlocked(false)
  }

  const handlePause = () => {
    const video = videoRef.current
    if (!playing || !video || video.ended || document.visibilityState !== "visible") return

    setPlayingMp4(null)
    setAutoplayBlocked(true)
  }

  const handleManualPlayback = () => {
    const video = videoRef.current
    if (!video || !playing) return

    video.muted = true
    void video.play().catch(() => {
      setPlayingMp4(null)
      setAutoplayBlocked(true)
    })
  }

  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 overflow-hidden bg-black ${
        autoplayBlocked && playing ? "pointer-events-auto cursor-pointer" : ""
      }`}
      onClick={autoplayBlocked && playing ? handleManualPlayback : undefined}
    >
      {poster && (
        <Image
          key={poster}
          src={poster}
          alt={label}
          fill
          sizes="(max-width: 640px) 50vw, 240px"
          loading="eager"
          draggable={false}
          unoptimized
          className={`pointer-events-none absolute inset-0 h-full w-full object-cover transition-opacity duration-150 ${
            hasStartedPlayback ? "opacity-0" : "opacity-100"
          }`}
        />
      )}
      <video
        key={mp4}
        ref={videoRef}
        className={`pointer-events-none absolute inset-0 h-full w-full object-cover transition-opacity duration-150 ${
          showPoster ? "opacity-0" : "opacity-100"
        }`}
        aria-hidden="true"
        poster={poster}
        muted
        loop
        playsInline
        preload="auto"
        autoPlay={playing}
        onPlaying={handlePlaying}
        onPause={handlePause}
      >
        <source src={mp4} type="video/mp4" />
      </video>
      {autoplayBlocked && playing && <VideoAutoplayFallback />}
    </div>
  )
}
